import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Shield, Mail, Smartphone, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const SecurityPage = () => {
  const { user, setupMfa, disableMfa } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showTotpDialog, setShowTotpDialog] = useState(false);
  const [totpData, setTotpData] = useState(null);

  const handleEnableMfa = async (type) => {
    setIsLoading(true);
    try {
      const result = await setupMfa(type);
      if (type === 'totp' && result.qr_code) {
        setTotpData(result);
        setShowTotpDialog(true);
      } else {
        toast.success(`${type === 'email' ? 'Email' : 'Authenticator'} MFA enabled successfully`);
      }
    } catch (error) {
      toast.error('Failed to enable MFA');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    setIsLoading(true);
    try {
      await disableMfa();
      toast.success('MFA disabled');
    } catch (error) {
      toast.error('Failed to disable MFA');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="security-page">
      {/* Page Header */}
      <div className="space-y-1">
        <h1 
          className="text-3xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Security Settings
        </h1>
        <p className="text-slate-600">
          Manage your account security and multi-factor authentication
        </p>
      </div>

      {/* MFA Status */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Shield className="w-5 h-5 text-violet-600" />
            Multi-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Status */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              {user?.mfa_enabled ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <p className="font-medium text-slate-900">
                  MFA is {user?.mfa_enabled ? 'enabled' : 'disabled'}
                </p>
                {user?.mfa_enabled && (
                  <p className="text-sm text-slate-500">
                    Using {user.mfa_type === 'totp' ? 'Authenticator App' : 'Email OTP'}
                  </p>
                )}
              </div>
            </div>
            {user?.mfa_enabled && (
              <Button
                variant="outline"
                onClick={handleDisableMfa}
                disabled={isLoading}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="disable-mfa-btn"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable'}
              </Button>
            )}
          </div>

          {/* MFA Options */}
          {!user?.mfa_enabled && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Choose your preferred MFA method:</p>
              
              {/* Email OTP Option */}
              <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-violet-200 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Email OTP</p>
                    <p className="text-sm text-slate-500">Receive a code via email on each login</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleEnableMfa('email')}
                  disabled={isLoading}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="enable-email-mfa-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable'}
                </Button>
              </div>

              {/* TOTP Option */}
              <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-violet-200 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Authenticator App</p>
                    <p className="text-sm text-slate-500">Use Google Authenticator, Authy, etc.</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleEnableMfa('totp')}
                  disabled={isLoading}
                  variant="outline"
                  data-testid="enable-totp-mfa-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* TOTP Setup Dialog */}
      <Dialog open={showTotpDialog} onOpenChange={setShowTotpDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              Set Up Authenticator App
            </DialogTitle>
            <DialogDescription>
              Scan this QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {totpData?.qr_code && (
              <div className="flex justify-center">
                <img 
                  src={totpData.qr_code} 
                  alt="TOTP QR Code"
                  className="w-48 h-48 border border-slate-200 rounded-lg"
                />
              </div>
            )}
            {totpData?.secret && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Manual entry code:</Label>
                <code className="block w-full p-3 bg-slate-100 rounded-lg text-center font-mono text-sm select-all">
                  {totpData.secret}
                </code>
              </div>
            )}
            <Button 
              onClick={() => {
                setShowTotpDialog(false);
                toast.success('Authenticator MFA enabled successfully');
              }}
              className="w-full bg-violet-600 hover:bg-violet-700"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Info */}
      <Card className="bg-slate-900 text-white border-0">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why enable MFA?
          </h3>
          <p className="text-slate-300 text-sm">
            Multi-factor authentication adds an extra layer of security by requiring a second form of 
            verification in addition to your password. This helps protect your S1000D project data 
            even if your password is compromised.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityPage;
